import { UploadFile } from 'antd/es/upload/interface';
import { AxiosError, CancelTokenSource } from 'axios';
import apiClient, { CCSRFToken, makeQueryString, WrappingApiClient } from 'libs/api/apiClient';
import { handleHTTPError } from 'libs/api/errorHandle';
import { TListRequestParams, TListResponse } from 'modules/types';
import {
  TUser,
  TUserLogin,
  TMentorCreate,
  TMentor,
  TMentee,
  TSimpleMentor,
  TMenteeCreate,
  TSimpleMentee,
  TSetInterestedMentor,
  TActivateMentoring,
  TSetInterestedIntern,
  TUserUpdate,
  TUpdateFullname,
  TUpdateNickname,
  TUpdateTimezone,
  TIntroductionUpdate,
  TUpdateProfileImage,
  TPasswordChange,
  TPasswordReset,
  TPasswordUpdate,
  TVerificationEmail,
  TCreditTransaction,
  TCreditExchangeCreate,
  TSubmitVerification,
  TValidatePassword,
  TFormattedSchedule,
  TCreditExchangeInfo,
  TMenteeUpdate,
  TMentorUpdate,
  TSendSMS,
  TReceiveSMS,
  TChangePrimaryField,
  TgetCompaniesParam,
  TKakaoLogin,
  TMenteeCreateKakao,
  TMentorCreateKakao,
} from 'modules/user';

/**
 * Mentor
 */

export const mentorCreate = (params: TMentorCreate, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentor/create', params, {
    cancelToken: cancel?.token,
  });
};

export const mentorCreateKakao = (params: TMentorCreateKakao, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentor/create_kakao', params, {
    cancelToken: cancel?.token,
  });
};

export const getMentor = (id: number, cancel?: CancelTokenSource) => {
  return apiClient.get<TMentor>(`/mentor/${id}`, {
    cancelToken: cancel?.token,
  });
};

export const getMentors = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentor>>('/mentors', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getInterestedMentors = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentor>>('/mentors?is_interested=true', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getPopularMentors = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentor>>('/mentors?popular=true', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getMentorsForLanding = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentor>>('/mentor_list_for_landing', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getFormattedSchedules = (id: number, cancel?: CancelTokenSource) => {
  return apiClient.get<Array<TFormattedSchedule>>(`/mentor/${id}/formatted_schedules`, {
    cancelToken: cancel?.token,
  });
};

export const setInterestedIntern = (params: TSetInterestedIntern, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentor/set_interested_intern', params, {
    cancelToken: cancel?.token,
  });
};

export const getCurrentMentor = (id: number, cancel?: CancelTokenSource) => {
  return apiClient.get<TMentor>(`/mentor/current`, {
    cancelToken: cancel?.token,
  });
};

export const updateMentor = (params: TMentorUpdate, cancel?: CancelTokenSource) => {
  return apiClient.put(`/mentor/current`, params, {
    cancelToken: cancel?.token,
  });
};

export const activateMentoring = (params: TActivateMentoring, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentor/activate_mentoring', params, {
    cancelToken: cancel?.token,
  });
};

export const updateIntroduction = (params: TIntroductionUpdate, cancel?: CancelTokenSource) => {
  return apiClient.post(`/mentor/introduction/update`, params, {
    cancelToken: cancel?.token,
  });
};

/**
 * Mentee
 */

export const menteeCreate = (params: TMenteeCreate, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentee/create', params, {
    cancelToken: cancel?.token,
  });
};

export const menteeCreateKakao = (params: TMenteeCreateKakao, cancel?: CancelTokenSource) => {
  console.log('menteeCreateKakao ===========================>');
  return apiClient.post('/mentee/create_kakao', params, {
    cancelToken: cancel?.token,
  });
};

export const setInterestedMentor = (params: TSetInterestedMentor, cancel?: CancelTokenSource) => {
  return apiClient.post('/mentee/set_interested_mentor', params, {
    cancelToken: cancel?.token,
  });
};

export const getMentee = (id: number, cancel?: CancelTokenSource) => {
  return apiClient.get<TMentee>(`/mentee/${id}`, {
    cancelToken: cancel?.token,
  });
};

export const getCurrentMentee = (cancel?: CancelTokenSource) => {
  return apiClient.get<TMentee>(`/mentee/current`, {
    cancelToken: cancel?.token,
  });
};

export const getMentees = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TMentee>>('/mentees', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getInterns = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentee>>('/interns', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getInterestedInterns = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TSimpleMentee>>('/interns?is_interested=true', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const updateMentee = (params: TMenteeUpdate, cancel?: CancelTokenSource) => {
  return apiClient.put(`/mentee/current`, params, {
    cancelToken: cancel?.token,
  });
};

/**
 * User
 */

export const getUser = () => {
  return apiClient.get<TUser>(`/user`);
};

export const updateUser = (params: TUserUpdate, cancel?: CancelTokenSource) => {
  return apiClient.put(`/user`, params, {
    cancelToken: cancel?.token,
  });
};

export const updateFullname = (params: TUpdateFullname, cancel?: CancelTokenSource) => {
  return apiClient.post(`/user/fullname/update`, params, {
    cancelToken: cancel?.token,
  });
};

export const updateNickname = (params: TUpdateNickname, cancel?: CancelTokenSource) => {
  return apiClient.post(`/user/nickname/update`, params, {
    cancelToken: cancel?.token,
  });
};

export const updateTimezone = (params: TUpdateTimezone, cancel?: CancelTokenSource) => {
  return apiClient.post(`/user/timezone/update`, params, {
    cancelToken: cancel?.token,
  });
};

export const updateProfileImage = (params: TUpdateProfileImage, cancel?: CancelTokenSource) => {
  return apiClient.post(`/user/profile_image/update`, params, {
    cancelToken: cancel?.token,
  });
};

export const checkDuplicateUserid = (userid: string, cancel?: CancelTokenSource) => {
  return apiClient.get<boolean>(`/userid/duplicate_check/${userid}`, {
    cancelToken: cancel?.token,
  });
};

export const passwordChange = (params: TPasswordChange) => {
  return apiClient.post(`/password/change`, params);
};

export const passwordReset = (params: TPasswordReset) => {
  return apiClient.post(`/password/reset`, params);
};

export const passwordUpdate = (uidb64: string, token: string, params: TPasswordUpdate) => {
  return apiClient.post(`/password/update/${uidb64}/${token}`, params);
};

export const getSampleImages = (cancel?: CancelTokenSource) => {
  return apiClient.get<Array<UploadFile>>(`/user/sample_images`, {
    cancelToken: cancel?.token,
  });
};

//탈퇴 api
export const deleteAccount = () => {
  return apiClient.delete('user/deactivate');
};

export const changePrimaryField = (params: TChangePrimaryField, cancel?: CancelTokenSource) => {
  return apiClient.post(`/user/change_primary`, params, {
    cancelToken: cancel?.token,
  });
};

export const main = () =>
  wrapPromise(
    apiClient.get<TUser>('/user').then(
      async (response) => {
        const authorization = response.headers.authorization;
        if (authorization) {
          window.localStorage.setItem(CCSRFToken, authorization.split('Basic ')[1]);
        }

        return response.data;
      },
      (e) => e,
    ),
  );

export function wrapPromise(promise: any) {
  let status = 'pending';
  let result: any;
  let suspender = promise.then(
    (r: any) => {
      status = 'success';
      result = r;
    },
    (e: any) => {
      status = 'error';
      result = e;
    },
  );

  return {
    read() {
      if (status === 'pending') {
        throw suspender;
      } else if (status === 'error') {
        throw handleHTTPError(result as AxiosError);
      } else if (status === 'success') {
        return result;
      }
    },
  };
}

/**
 * Authentication
 */

export const userLogin = (params: TUserLogin, cancel?: CancelTokenSource) => {
  return apiClient.post('/user/login', params, {
    cancelToken: cancel?.token,
  });
};
export const userKakaoLogin = (params: TKakaoLogin, cancel?: CancelTokenSource) => {
  return apiClient.post('/user/login_kakao', params, {
    cancelToken: cancel?.token,
  });
};

export const userKakaoCheck = (params: TSendSMS) => {
  return apiClient.post('/user/member_check', params);
};

export const userLogout = () => apiClient.post('/user/logout');

export const userActivate = (uidb64: string, token: string) => {
  return apiClient.post(`/user/activate/${uidb64}/${token}`);
};

/**
 * Verification
 */
export const sendVerificationEmail = (params: TVerificationEmail) => {
  return apiClient.post('/verification/send_email', params);
};

export const verificationEmail = (uidb64: string) => {
  return apiClient.post(`/verification/email/${uidb64}`);
};

export const submitVerification = (params: TSubmitVerification, cancel?: CancelTokenSource) => {
  /* 전/현직 인증에서 다음 단계 버튼 클릭시 호출됨 */
  return apiClient.post('/verification/submit', params, {
    cancelToken: cancel?.token,
  });
};

export const validatePassword = (params: TValidatePassword, cancel?: CancelTokenSource) => {
  return apiClient.post(`/password/validate`, params, {
    cancelToken: cancel?.token,
  });
};

/**
 * Credit
 */

export const getCreditBalance = ({ field }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<number>(`/credit/balance?${makeQueryString(field)}`, {
    cancelToken: cancel?.token,
  });
};

export const getMenteeCreditTransactions = (
  { params, field }: TListRequestParams,
  cancel?: CancelTokenSource,
) => {
  return apiClient.get<TListResponse<TCreditTransaction>>(
    `/credit/mentee/transactions?${makeQueryString(field)}`,
    {
      params: params,
      cancelToken: cancel?.token,
    },
  );
};

// 수강권 구매 및 사용 내역에서 결제 내역 로그 불러오기
export const getMenteePurchaseLog = ({ params, field }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get(`/credit/order_list?${makeQueryString(field)}`, {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getMentorCreditTransactions = ({ params }: TListRequestParams, cancel?: CancelTokenSource) => {
  return apiClient.get<TListResponse<TCreditTransaction>>('/credit/mentor/transactions', {
    params: params,
    cancelToken: cancel?.token,
  });
};

export const getCreditExchangeInfo = (cancel?: CancelTokenSource) => {
  return apiClient.get<TCreditExchangeInfo>(`/credit/exchange_info`, {
    cancelToken: cancel?.token,
  });
};

export const creditExchangeCreate = (params: TCreditExchangeCreate, cancel?: CancelTokenSource) => {
  return apiClient.post('/credit/exchange/create', params, {
    cancelToken: cancel?.token,
  });
};

/**
 * 가입가능 회사
 */

export const verifiableCompanyName = (params?: TgetCompaniesParam, cancel?: CancelTokenSource) => {
  return apiClient.get<any>(`/mentor/verifiable_company_name?${makeQueryString(params)}`, {
    cancelToken: cancel?.token,
  });
};

/*
 * 문자 인증
 */
export const sendSMSVerification = (params: TSendSMS, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/send_verification', params, {
    cancelToken: cancel?.token,
  });
};

export const sendSMSVerificationToFindUserId = (params: TSendSMS, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/send_verification/find_userid', params, {
    cancelToken: cancel?.token,
  });
};

export const receiveSMSVerification = (params: TReceiveSMS, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/receive_verification', params, {
    cancelToken: cancel?.token,
  });
};

export const receiveSMSVerificationKakao = (params: TReceiveSMS, type: string, token: string, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/receive_verification_kakao', {
    code : params.code, 
    country_code: params.country_code, 
    phone: params.phone, 
    type: type,
    token: token
  }, {
    cancelToken: cancel?.token,
  });
};

export const receiveSMSVerificationToFindUserId = (params: TReceiveSMS, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/receive_verification_userid', params, {
    cancelToken: cancel?.token,
  });
};

export const receiveSMSVerificationToUpdatePhone = (params: TReceiveSMS, cancel?: CancelTokenSource) => {
  return apiClient.post('/notification/sms/receive_verification_phone', params, {
    cancelToken: cancel?.token,
  });
};

export const get150paymentUser = () => {
  return WrappingApiClient(apiClient.get('/payment/150_credit'));
};

